import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { ajaxPerso, confirmation_modal } from '../../../fnc'
import moment from 'moment';
import { NavLink } from 'react-router-dom';

const PROMO_REFRESH_INTERVAL = 60000;

const getImageSource = promo => {
	const firstImage = promo.images_json && promo.images_json[0];
	return firstImage? (firstImage.node? firstImage.node.originalSrc : firstImage.src) : null;
};

const formatPrice = price => {
	const numericPrice = Number(price);
	return Number.isFinite(numericPrice)? numericPrice.toFixed(2) : '—';
};

const formatDuration = (date, prefix) => {
	const seconds = moment(date).diff(moment(), 'seconds');
	const absoluteSeconds = Math.abs(seconds);
	const days = Math.floor(absoluteSeconds / 86400);
	const hours = Math.floor((absoluteSeconds % 86400) / 3600);
	const minutes = Math.floor((absoluteSeconds % 3600) / 60);
	const parts = [];
	if(days) parts.push(`${days}d`);
	if(hours || days) parts.push(`${hours}h`);
	parts.push(`${minutes}m`);
	return `${prefix} ${parts.join(' ')}`;
};

const PromoProduct = ({ promo, shopSelected }) => {
	const imageSource = getImageSource(promo);
	return <div className="promo-list__product">
		{imageSource? <img src={imageSource} alt="" className="promo-list__image" /> : <div className="promo-list__image promo-list__image--empty"><i className="bi bi-image" aria-hidden="true" /></div>}
		<div>
			<a className="promo-list__product-title" target="_blank" rel="noopener noreferrer" href={`https://${shopSelected.account_name}/products/${promo.handle}`}>{promo.title || 'Untitled product'}</a>
			<div className="promo-list__sku">{promo.sku || 'No SKU'}</div>
			<NavLink className="promo-list__product-link" to={`/shopify/${promo.id_product_shopify}`}>Edit product</NavLink>
		</div>
	</div>;
};

export default function ListPromo(props){
	const { info, refreshVersion, shopSelected } = props;
	const [promoList, setPromoList] = useState([]);
	const [showEnded, setShowEnded] = useState(false);
	const [selectedVariants, setSelectedVariants] = useState([]);
	const [hasLoaded, setHasLoaded] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [lastUpdated, setLastUpdated] = useState(null);
	const [pendingEndVariants, setPendingEndVariants] = useState([]);
	const mountedRef = useRef(false);
	const requestInFlightRef = useRef(false);
	const requestVersionRef = useRef(0);

	const loadPromo = useCallback(({ silent = false } = {}) => {
		if(!shopSelected || !shopSelected.id || requestInFlightRef.current) return;
		requestInFlightRef.current = true;
		const requestVersion = requestVersionRef.current + 1;
		requestVersionRef.current = requestVersion;
		if(!silent) setIsRefreshing(true);

		ajaxPerso({
			api: 'shopify',
			trigger: 'get_promo',
			special: 'special_get_promo',
			shop: shopSelected.id,
			ended: showEnded? 1 : 0,
			include_product: true
		}, response => {
			if(!mountedRef.current || requestVersion !== requestVersionRef.current) return;
			requestInFlightRef.current = false;
			setIsRefreshing(false);
			setHasLoaded(true);
			if(response && response.success){
				setPromoList(response.data || []);
				setLastUpdated(new Date());
				setPendingEndVariants([]);
			}else if(response && response.error){
				info({error: response.error});
			}
		});
	}, [info, shopSelected, showEnded]);

	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	useEffect(() => {
		requestInFlightRef.current = false;
		setPromoList([]);
		setSelectedVariants([]);
		setPendingEndVariants([]);
		setHasLoaded(false);
		setLastUpdated(null);
		loadPromo();
	}, [loadPromo, refreshVersion, shopSelected]);

	useEffect(() => {
		const refreshWhenVisible = () => {
			if(!document.hidden) loadPromo({silent: true});
		};
		const interval = window.setInterval(() => {
			if(!document.hidden) loadPromo({silent: true});
		}, PROMO_REFRESH_INTERVAL);
		document.addEventListener('visibilitychange', refreshWhenVisible);
		return () => {
			window.clearInterval(interval);
			document.removeEventListener('visibilitychange', refreshWhenVisible);
		};
	}, [loadPromo]);

	const awaitingPromos = useMemo(() => promoList.filter(promo => !promo.started && !promo.ended), [promoList]);
	const activePromos = useMemo(() => promoList.filter(promo => promo.started), [promoList]);
	const selectedPromos = useMemo(() => activePromos.filter(promo => selectedVariants.includes(promo.id_variant_shopify)), [activePromos, selectedVariants]);

	const queueEndPromo = useCallback((promo, event) => {
		if(!props.shopSelected || !promo.id_variant_shopify) return;
		confirmation_modal(props, closeModal => {
			closeModal();
			setPendingEndVariants(current => current.includes(promo.id_variant_shopify)? current : [...current, promo.id_variant_shopify]);
			ajaxPerso({
				api: 'shopify',
				shop: props.shopSelected.id,
				trigger: 'end_promo',
				method: 'POST',
				id_variant_shopify: promo.id_variant_shopify
			}, response => {
				if(response && response.success){
					props.info({success: 'Promo marked to end. The server will apply the change on its next run.'});
					window.setTimeout(() => loadPromo({silent: true}), 1000);
				}else{
					setPendingEndVariants(current => current.filter(id => id !== promo.id_variant_shopify));
					props.info({error: (response && (response.error || response.errors)) || 'Unable to mark this promo for removal.'});
				}
			});
		}, event);
	}, [loadPromo, props]);

	const removeAwaitingPromo = useCallback((promo, event) => {
		confirmation_modal(props, closeModal => {
			closeModal();
			ajaxPerso({
				api: 'shopify',
				trigger: 'end_db_promo',
				shop: props.shopSelected.id,
				id: promo.id
			}, response => {
				if(response && response.data && response.data.deleted){
					props.info({success: 'Scheduled promo removed.'});
					loadPromo({silent: true});
				}else props.info({error: (response && (response.error || response.errors)) || 'Unable to remove this promo.'});
			});
		}, event);
	}, [loadPromo, props]);

	const queueSelectedForEnd = event => {
		confirmation_modal(props, closeModal => {
			closeModal();
			const promosToEnd = selectedPromos.filter(promo => !pendingEndVariants.includes(promo.id_variant_shopify));
			setSelectedVariants([]);
			setPendingEndVariants(current => [...new Set([...current, ...promosToEnd.map(promo => promo.id_variant_shopify)])]);
			promosToEnd.forEach(promo => {
				ajaxPerso({api: 'shopify', shop: props.shopSelected.id, trigger: 'end_promo', method: 'POST', id_variant_shopify: promo.id_variant_shopify}, response => {
					if(!response || !response.success) setPendingEndVariants(current => current.filter(id => id !== promo.id_variant_shopify));
				});
			});
			props.info({success: `${promosToEnd.length} promo${promosToEnd.length === 1? '' : 's'} marked to end. The server will apply the change on its next run.`});
			window.setTimeout(() => loadPromo({silent: true}), 1000);
		}, event);
	};

	const toggleSelected = variantId => setSelectedVariants(current => current.includes(variantId)? current.filter(id => id !== variantId) : [...current, variantId]);
	const selectedCount = selectedPromos.length;

	const renderStatus = promo => {
		if(promo.ended) return <span className="promo-list__status promo-list__status--ended">Ended</span>;
		if(pendingEndVariants.includes(promo.id_variant_shopify)) return <span className="promo-list__status promo-list__status--ending">Ending soon</span>;
		if(!promo.started) return <span className="promo-list__status promo-list__status--awaiting">Awaiting activation</span>;
		return <span className="promo-list__status promo-list__status--active">Active</span>;
	};

	const renderPromoRow = (promo, isAwaiting) => <tr key={`promoList_${promo.id}`}>
		<td className="promo-list__select-cell" data-label="Select">
			{!isAwaiting && !promo.ended && <label className="promo-list__checkbox"><input type="checkbox" checked={selectedVariants.includes(promo.id_variant_shopify)} onChange={() => toggleSelected(promo.id_variant_shopify)} aria-label={`Select ${promo.title || 'promo'}`} /><span /></label>}
		</td>
		<td data-label="Product"><PromoProduct promo={promo} shopSelected={props.shopSelected} /></td>
		<td data-label="Status">{renderStatus(promo)}</td>
		<td data-label="Reduction"><strong>{promo.change_type === 'percentage'? `${promo.change_value}% off` : `${formatPrice(promo.change_value)} fixed price`}</strong><small>{promo.change_type === 'percentage'? 'Percentage discount' : 'Set sale price'}</small></td>
		<td data-label="Price"><span className="promo-list__price-before">{formatPrice(promo.temp_original_price || promo.original_price)}</span><strong>{formatPrice(promo.new_price_calculated)}</strong></td>
		<td data-label="Schedule"><strong>{moment(promo.starting_date).format('D MMM, HH:mm')}</strong><small>{isAwaiting? formatDuration(promo.starting_date, 'Starts in') : formatDuration(promo.starting_date, 'Running for')}</small></td>
		<td data-label="Ends"><strong>{moment(promo.ending_date).format('D MMM, HH:mm')}</strong><small>{promo.ended? 'Completed' : formatDuration(promo.ending_date, 'Ends in')}</small></td>
		<td className="promo-list__actions" data-label="Actions">
			{isAwaiting? <button type="button" className="btn btn-outline-danger btn-sm" onClick={event => removeAwaitingPromo(promo, event)} title="Remove scheduled promo"><FontAwesomeIcon icon={faTrashAlt} /><span>Remove</span></button> : !promo.ended && <button type="button" className="btn btn-outline-danger btn-sm" onClick={event => queueEndPromo(promo, event)} disabled={pendingEndVariants.includes(promo.id_variant_shopify)}>{pendingEndVariants.includes(promo.id_variant_shopify)? 'Ending…' : <><FontAwesomeIcon icon={faTrashAlt} /><span>End</span></>}</button>}
		</td>
	</tr>;

	return <section className="promo-list" aria-label="Promotion status">
		<div className="promo-list__header">
			<div>
				<h2>Scheduled and active promos</h2>
				<p>{lastUpdated? `Last checked ${moment(lastUpdated).format('HH:mm')}. Status refreshes quietly every minute while this page is open.` : 'Loading the current promo status…'}</p>
			</div>
			<div className="promo-list__summary">
				<span><strong>{awaitingPromos.length}</strong> scheduled</span>
				<span><strong>{activePromos.filter(promo => !promo.ended).length}</strong> active</span>
			</div>
		</div>
		<div className="promo-list__card">
			<div className="promo-list__toolbar">
				<label className="promo-list__ended-toggle"><input type="checkbox" checked={showEnded} onChange={event => setShowEnded(event.target.checked)} /><span className="promo-list__switch" aria-hidden="true" /><span>Show ended promos</span></label>
				<div className="promo-list__toolbar-actions">
					{selectedCount > 0 && <button type="button" className="btn btn-danger btn-sm" onClick={queueSelectedForEnd}>End {selectedCount} selected</button>}
					<button type="button" className="promo-list__refresh btn btn-sm" onClick={() => loadPromo()} disabled={isRefreshing} aria-label="Refresh promotion status"><FontAwesomeIcon icon={faSyncAlt} spin={isRefreshing} /><span>{isRefreshing? 'Refreshing' : 'Refresh'}</span></button>
				</div>
			</div>
			{!hasLoaded? <div className="promo-list__empty"><span className="spinner-border spinner-border-sm mr-2" role="status" /> Loading promotions…</div> : promoList.length === 0? <div className="promo-list__empty"><i className="bi bi-tag" aria-hidden="true" /><strong>{showEnded? 'No ended promos for this website.' : 'No scheduled or active promos for this website.'}</strong><span>Create a promo above and it will appear here immediately.</span></div> : <>
				{awaitingPromos.length > 0 && <div className="promo-list__section-label"><span>Awaiting activation</span><small>The server checks and applies scheduled promos every minute.</small></div>}
				{awaitingPromos.length > 0 && <div className="table-responsive"><table className="table promo-list__table mb-0"><thead><tr><th /><th>Product</th><th>Status</th><th>Reduction</th><th>Price</th><th>Starts</th><th>Ends</th><th>Actions</th></tr></thead><tbody>{awaitingPromos.map(promo => renderPromoRow(promo, true))}</tbody></table></div>}
				{activePromos.length > 0 && <div className="promo-list__section-label"><span>{showEnded? 'Ended promos' : 'Active promos'}</span><small>{showEnded? 'Completed promos are retained here for reference.' : 'Choose one or more promos to queue them for removal.'}</small></div>}
				{activePromos.length > 0 && <div className="table-responsive"><table className="table promo-list__table mb-0"><thead><tr><th /><th>Product</th><th>Status</th><th>Reduction</th><th>Price</th><th>Starts</th><th>Ends</th><th>Actions</th></tr></thead><tbody>{activePromos.map(promo => renderPromoRow(promo, false))}</tbody></table></div>}
			</>}
		</div>
	</section>;
}
